import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ExportCsvButton } from "../components/ExportCsvButton";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { activeFilterClass } from "../lib/filterStyles";
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  Building,
  Phone,
  Mail,
  MapPin,
  ShoppingCart
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const Clients = () => {
  const { hasRole, canEdit, canDelete } = useAuth();
  // Iter54 P2 — filtre persistate în URL (refresh/back nu mai pierde căutarea)
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get("page") || "1", 10) || 1,
    limit: parseInt(searchParams.get("limit") || "20", 10) || 20,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [clientTypeFilter, setClientTypeFilter] = useState(searchParams.get("client_type") || "all");
  const [deleteDialog, setDeleteDialog] = useState(null);

  // Iter54 P2 — sync stare → URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTerm) next.set("search", searchTerm);
    if (clientTypeFilter && clientTypeFilter !== "all") next.set("client_type", clientTypeFilter);
    if (pagination.page > 1) next.set("page", String(pagination.page));
    if (pagination.limit !== 20) next.set("limit", String(pagination.limit));
    setSearchParams(next, { replace: true });
  }, [searchTerm, clientTypeFilter, pagination.page, pagination.limit, setSearchParams]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", pagination.page);
      params.set("limit", pagination.limit);
      
      if (searchTerm) params.set("search", searchTerm);
      if (clientTypeFilter && clientTypeFilter !== "all") {
        params.set("client_type", clientTypeFilter);
      }
      
      const response = await axios.get(`${API_URL}/clients?${params.toString()}`);
      setClients(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error("Eroare la încărcarea clienților");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, clientTypeFilter]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    
    try {
      await axios.delete(`${API_URL}/clients/${deleteDialog}`);
      toast.success("Client șters");
      setDeleteDialog(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergerea clientului");
    }
  };

  const getClientName = (client) => {
    if (client.client_type === "PF") {
      return client.full_name || `${client.last_name || ""} ${client.first_name || ""}`.trim() || client.cnp || "-";
    }
    return client.company_name || client.full_name || client.last_name || client.cui || "-";
  };

  const getClientIdentifier = (client) => {
    if (client.client_type === "PF") {
      return client.cnp || "-";
    }
    return client.cui || "-";
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Clienți</h1>
          <p className="text-slate-500 mt-1">
            Gestionează clienții și datele lor de contact
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter53 P40 — Universal CSV export button */}
          <ExportCsvButton entity="clients" />
          <Link to="/clients/new">
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-client-btn">
              <Plus className="h-4 w-4 mr-2" />
              Client Nou
            </Button>
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Caută după nume, CNP, CUI, telefon, email..."
                className={`pl-10 ${activeFilterClass(searchTerm)}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            <Select value={clientTypeFilter} onValueChange={setClientTypeFilter}>
              <SelectTrigger
                className={`w-48 ${activeFilterClass(clientTypeFilter === "all" ? "" : clientTypeFilter)}`}
                data-testid="client-type-filter"
              >
                <SelectValue placeholder="Tip client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate tipurile</SelectItem>
                <SelectItem value="PF">Persoană Fizică</SelectItem>
                <SelectItem value="PJ">Persoană Juridică</SelectItem>
              </SelectContent>
            </Select>
            
            <Button type="submit" data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results Counter */}
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-lg px-4 py-2" data-testid="results-counter">
          <Users className="h-4 w-4 mr-2" />
          {pagination.total} clienți
        </Badge>
      </div>

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          {initialLoad && loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nu există clienți</h3>
              <p className="text-slate-500 mb-4">Adaugă primul client pentru a începe</p>
              <Link to="/clients/new">
                <Button data-testid="empty-new-client-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Adaugă Client
                </Button>
              </Link>
            </div>
          ) : (
            <div className={`overflow-auto transition-opacity duration-200 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Tip
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Nume / Denumire
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      CNP / CUI
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Locație
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Vânzări
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {clients.map((client) => (
                    <tr
                      key={client.id}
                      className="hover:bg-slate-50/50 transition-colors"
                      data-testid={`client-row-${client.id}`}
                    >
                      <td className="px-4 py-3">
                        <Badge variant="outline">
                          {client.client_type === "PF" ? "Persoană Fizică" : "Persoană Juridică"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/clients/${client.id}`}
                          className="font-medium text-slate-900 hover:text-sky-600"
                        >
                          {getClientName(client)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 font-mono">
                        {getClientIdentifier(client)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>{client.county || "-"}, {client.locality || "-"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {client.phone_mobile && (
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Phone className="h-3.5 w-3.5" />
                              <span>{client.phone_mobile}</span>
                            </div>
                          )}
                          {client.email && (
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Mail className="h-3.5 w-3.5" />
                              <span className="truncate max-w-[150px]">{client.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Iter54 P2 — count vânzări cu link direct la lista filtrată */}
                      <td className="px-4 py-3">
                        {client.sales_count > 0 ? (
                          <Link
                            to={`/sales?client_id=${client.id}`}
                            data-testid={`client-sales-link-${client.id}`}
                          >
                            <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-200 cursor-pointer">
                              <ShoppingCart className="h-3 w-3 mr-1" />
                              {client.sales_count}
                            </Badge>
                          </Link>
                        ) : (
                          <span className="text-sm text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`actions-${client.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/clients/${client.id}`}>
                                <Eye className="h-4 w-4 mr-2" />
                                Vizualizează
                              </Link>
                            </DropdownMenuItem>
                            {canEdit() && (
                              <DropdownMenuItem asChild>
                                <Link to={`/clients/${client.id}?edit=1`}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editează
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {canDelete() && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  onClick={() => setDeleteDialog(client.id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Șterge
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {Math.ceil(pagination.total / pagination.limit) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Afișare {((pagination.page - 1) * pagination.limit) + 1} -{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} din{" "}
            {pagination.total} clienți
          </p>
          <div className="flex items-center gap-2">
            {/* Iter54 P2 — selector mărime pagină */}
            <Select
              value={String(pagination.limit)}
              onValueChange={(v) => setPagination(prev => ({ ...prev, limit: parseInt(v, 10), page: 1 }))}
            >
              <SelectTrigger className="w-24 h-8" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20 / pag</SelectItem>
                <SelectItem value="50">50 / pag</SelectItem>
                <SelectItem value="100">100 / pag</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-700 px-2">
              Pagina {pagination.page} din {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi acest client? Această acțiune nu poate fi anulată.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
